import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useContext, useEffect, useRef, useMemo } from 'react'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { userSelector } from '@src/selectors/app'
import {
  CoachesCoachingKind,
  EhrAppointmentKind,
  EhrCoachSpecialty,
  UiStateNames,
} from '@src/types'
import { useDispatchAsync } from '@src/utils/dispatch'
import { coachAssignmentForCoachingKindSelector } from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { RootStackParamList } from '@src/navigation/types'
import { ErrorMessages } from '@src/config/logger'
import { NavigatorContext, useSnack } from '@src/utils/navigatorContext'
import { AllCoachProfilesQuery } from '@src/graphql/fetchAllCoachProfiles.generated'

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 2

interface UseCoachSwitchParams {
  coachingKind?: CoachesCoachingKind
}

export const useCoachSwitch = ({
  coachingKind = CoachesCoachingKind.Mnt,
}: UseCoachSwitchParams = {}) => {
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const showSnack = useSnack()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const switchingRef = useRef(false)

  const user = useSelector(userSelector)
  const coachAssignmentSelector = useMemo(
    () => coachAssignmentForCoachingKindSelector(coachingKind),
    [coachingKind],
  )
  const coachAssignment = useSelector(coachAssignmentSelector)

  useEffect(
    () => () => {
      if (switchingRef.current) {
        disableLoader()
        switchingRef.current = false
      }
    },
    [disableLoader],
  )

  const fetchCoachProfiles = useCallback(
    ({
      recommended = false,
      page = DEFAULT_PAGE,
      perPage = DEFAULT_PER_PAGE,
      specialties = [] as EhrCoachSpecialty[],
      appointmentKind,
    }: {
      recommended?: boolean
      page?: number
      perPage?: number
      specialties?: EhrCoachSpecialty[]
      appointmentKind?: EhrAppointmentKind
    }) => {
      return dispatchAsync<AllCoachProfilesQuery['allCoachProfiles']>({
        type: 'nutritionistHub/fetchAllCoachProfiles',
        payload: {
          appointmentKind,
          recommended,
          page,
          licensedState: user?.address?.state,
          perPage,
          specialties,
          excludedCoachIds: coachAssignment?.coach?.id ? [coachAssignment.coach.id] : [],
        },
      })
    },
    [dispatchAsync, user?.address?.state, coachAssignment],
  )

  const markAsDismissed = () => {
    dispatch({
      type: 'uiFlags/updateUiFlags',
      payload: {
        showNutritionistSwitchModal: false,
      },
    })
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.NutritionistSwitchModal,
        value: false,
      },
    })
  }

  const dismissNutritionistSwitch = () => {
    markAsDismissed()
    navigation.reset({
      index: 0,
      routes: [
        {
          name: 'Drawer',
          params: { screen: 'Dashboard', params: { screen: 'NutritionistHub' } },
        },
      ],
    })
  }

  const confirmSwitch = async ({
    coachIdToSwitchTo,
    appointmentKind,
    recurring,
    selectedSlot,
    appointmentIdsToReschedule,
    appointmentIdsToCancel,
  }: {
    coachIdToSwitchTo: string
    appointmentKind: EhrAppointmentKind
    selectedSlot: string
    recurring: boolean
    appointmentIdsToReschedule: string[]
    appointmentIdsToCancel: string[]
  }) => {
    if (switchingRef.current) {
      return
    }

    switchingRef.current = true
    enableLoader()

    try {
      await dispatchAsync({
        type: 'users/switchCoach',
        payload: {
          coachIdToSwitchTo,
          appointmentKind,
          recurring,
          selectedSlot,
          appointmentIdsToReschedule,
          appointmentIdsToCancel,
        },
      })
      dispatch({ type: 'uiFlags/fetch' })

      dismissNutritionistSwitch()
    } catch (error: any) {
      const errorMessage = error?.message || ErrorMessages.ServerError
      showSnack(errorMessage, null, 'error')
    } finally {
      disableLoader()
      switchingRef.current = false
    }
  }

  return {
    fetchCoachProfiles,
    confirmSwitch,
    dismissNutritionistSwitch,
  }
}
